import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Grid, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { UserRolePreferencesSettings } from '../../../../api/userrolepreferences/types';
import { getFilterKey } from '../../../../api/utils';
import { UNDEFINED_DISPLAY_KEY } from '../../../../constants';
import { GlobalFilterTypeHandle } from '../../../../context/types';
import { DataFields } from '../../../../types';
import {
  hierarchicalFieldFullLabel,
  isHierarchical,
  nonHierarchicalFieldLabel,
} from '../../../../utils';
import { teal2, unselectedFactboxColor } from '../../../theme';
import { FilterTypes } from '../filterbar/types';
import { FilterSection } from '../types';
import { DeleteDialog } from './DeleteDialog';
import { EditDialog } from './EditDialog';
import { MyFiltersGroup } from './types';

const StyledGrid = styled(Grid)`
  padding: 0.2em 0.5em 0.2em 1.5em;
  cursor: pointer;

  &:hover {
    background-color: ${unselectedFactboxColor};
  }
`;

const StyledDeleteIcon = styled(DeleteIcon)`
  font-size: 1.2em;
  cursor: pointer;

  &:hover {
    color: ${teal2};
  }
`;

const StyledEditIcon = styled(EditIcon)`
  font-size: 1.2em;
  cursor: pointer;

  &:hover {
    color: ${teal2};
  }
`;

interface MyFilterItemProps<T extends FilterTypes> {
  group: MyFiltersGroup<T>;
  filtersHandle: GlobalFilterTypeHandle<T>;
  currentRolePreferences: UserRolePreferencesSettings<T> | null;
  setExpand: (expand: boolean) => void;
  filterSections: FilterSection[];
}

export const MyFilterItem = <T extends FilterTypes>(
  props: MyFilterItemProps<T>
) => {
  const { t } = useTranslation();
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const {
    group,
    currentRolePreferences,
    filtersHandle,
    setExpand,
    filterSections,
  } = props;
  const [, dispatch] = filtersHandle;

  const defaultValue = t(UNDEFINED_DISPLAY_KEY);
  // This is needed for old myfilters that don't have the new key and label fields
  const groupFilters = group.filters.flatMap((f) => {
    const dimension = {
      dataType: f.dataType,
      dataField: f.property as DataFields,
    };
    const label =
      f.label ??
      (isHierarchical(dimension)
        ? hierarchicalFieldFullLabel(
            t,
            f.values as (string | null)[],
            dimension,
            defaultValue
          )
        : nonHierarchicalFieldLabel(
            t,
            f.values[0] as string | null,
            dimension,
            defaultValue
          ));
    const key = f.key ?? getFilterKey(dimension, f.values);
    return [{ ...f, key, label }];
  });

  return (
    <StyledGrid container item alignItems="center">
      <Grid
        item
        onClick={() => dispatch({ type: 'add-multiple', items: groupFilters })}
      >
        <Typography>{group.name}</Typography>
      </Grid>
      <Grid
        item
        alignItems="center"
        justifyContent="space-around"
        sx={{ padding: '0.2em 0 0 0.5em' }}
      >
        <StyledDeleteIcon
          onClick={(e) => {
            e.stopPropagation();
            setOpenDelete(true);
          }}
        />
        <StyledEditIcon
          onClick={(e) => {
            e.stopPropagation();
            setOpenEdit(true);
          }}
        />
      </Grid>
      <EditDialog<T>
        filtersHandle={filtersHandle}
        group={group}
        dialogHandle={[openEdit, setOpenEdit]}
        setExpand={setExpand}
        filterSections={filterSections}
      />
      <DeleteDialog<T>
        group={group}
        currentRolePreferences={currentRolePreferences}
        dialogHandle={[openDelete, setOpenDelete]}
      />
    </StyledGrid>
  );
};
